import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

export type FirstRunSummaryPhase =
  | 'idle'
  | 'learning'
  | 'ready'
  | 'empty'
  | 'done';

export interface FirstRunCapture {
  app: string;
  at: number;
}

const MAX_RECENT_CAPTURES = 5;

export const FIRST_RUN_WINDOW_MS = 2 * 60 * 1000;

class FirstRunSummaryStore {
  cutoffDate: string | null = null;
  phase: FirstRunSummaryPhase = 'idle';
  // Session-only: persisting it could rehydrate into a stuck learning state.
  summary: string | null = null;
  chatId: string | null = null;
  notified = false;
  // Persisted so a crash mid-seed doesn't create a duplicate chat next launch.
  seededCutoffDate: string | null = null;

  // Live-only; intentionally not persisted.
  recentCaptures: FirstRunCapture[] = [];

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'firstRunSummaryStore',
      properties: [
        'cutoffDate',
        'phase',
        'chatId',
        'notified',
        'seededCutoffDate',
      ],
      storage: window.localStorage,
    });
  }

  get isLearning() {
    return this.phase === 'learning';
  }

  get isReady() {
    return this.phase === 'ready';
  }

  beginFirstRun(cutoffISO: string) {
    this.cutoffDate = cutoffISO;
    this.phase = 'learning';
    this.summary = null;
    this.chatId = null;
    this.notified = false;
    this.seededCutoffDate = null;
    this.recentCaptures = [];
  }

  setSummary(summary: string) {
    this.summary = summary;
  }

  // Durable guard so a reload mid-create won't seed a second chat.
  markSeedStarted(cutoffISO: string) {
    this.seededCutoffDate = cutoffISO;
  }

  // Atomic "summary landed" transition. Claims the seed slot synchronously
  // (StrictMode / double-poll safe) and stores the summary. False if already
  // claimed for this cutoff or no longer learning.
  onSummaryArrived(cutoffISO: string, summary: string): boolean {
    if (this.phase !== 'learning' || this.seededCutoffDate === cutoffISO) {
      return false;
    }
    this.markSeedStarted(cutoffISO);
    this.setSummary(summary);
    return true;
  }

  markChatCreated(chatId: string) {
    this.chatId = chatId;
    this.phase = 'ready';
    this.recentCaptures = [];
  }

  markNotified() {
    this.notified = true;
  }

  // Terminal state distinct from 'done' so the UI can show a "nothing yet" note.
  markEmpty() {
    this.phase = 'empty';
    this.clearProgress();
  }

  // Window elapsed: settle to 'empty' unless a summary already landed.
  onWindowElapsed(): boolean {
    if (this.summary !== null) return false;
    this.markEmpty();
    return true;
  }

  markDone() {
    this.phase = 'done';
    this.clearProgress();
  }

  // First message sent from the empty state settles the run.
  settleEmptyOnSend() {
    if (this.phase !== 'empty') return;
    this.markDone();
  }

  // Drop in-progress data so terminal states carry no stale leftovers.
  private clearProgress() {
    this.summary = null;
    this.chatId = null;
    this.cutoffDate = null;
    this.notified = false;
    this.seededCutoffDate = null;
    this.recentCaptures = [];
  }

  pushCapture(app: string) {
    const capturedApp = app.trim();
    if (!capturedApp) return;
    const withoutDuplicate = this.recentCaptures.filter(
      (capture) => capture.app !== capturedApp
    );
    this.recentCaptures = [
      { app: capturedApp, at: Date.now() },
      ...withoutDuplicate,
    ].slice(0, MAX_RECENT_CAPTURES);
  }

  reset() {
    this.cutoffDate = null;
    this.phase = 'idle';
    this.summary = null;
    this.chatId = null;
    this.notified = false;
    this.seededCutoffDate = null;
    this.recentCaptures = [];
  }
}

export const firstRunSummaryStore = new FirstRunSummaryStore();
